/**
 * Parse a user-supplied URL into an embeddable representation for the video
 * block. Handles YouTube (watch / shorts / embed / youtu.be), Vimeo, and
 * direct video files (mp4 / webm / mov / m3u8).
 *
 * When `startSeconds` is provided the platform-appropriate offset is appended
 * to the embed URL: YouTube → `?start=N`, Vimeo → `#t=Ns`, native files →
 * `#t=N` (the media fragments spec handled natively by <video>). If the user
 * pasted a YouTube URL that already includes `t=` (the "Share at current
 * time" output), it's honored as the default and only overridden when the
 * caller passes a non-zero `startSeconds`.
 *
 * Returns:
 *   - { kind: 'iframe', src }  → render <iframe src={src} />
 *   - { kind: 'video', src }   → render <video src={src} controls />
 *   - null                     → not recognized
 */
import type React from 'react';

export type EmbedInfo =
  | { kind: 'iframe'; src: string }
  | { kind: 'video'; src: string }
  | null;

export function parseVideoUrl(url: string | undefined | null, startSeconds?: number | null): EmbedInfo {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const startNum = typeof startSeconds === 'number' && Number.isFinite(startSeconds) && startSeconds > 0
    ? Math.floor(startSeconds)
    : 0;

  // ─ YouTube ──────────────────────────────────────────────
  //   https://www.youtube.com/watch?v=ID
  //   https://youtu.be/ID
  //   https://www.youtube.com/embed/ID
  //   https://www.youtube.com/shorts/ID
  const yt =
    u.match(/[?&]v=([A-Za-z0-9_-]{6,})/) ||
    u.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ||
    u.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/) ||
    u.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (yt) {
    let effectiveStart = startNum;
    if (!effectiveStart) {
      const urlT = u.match(/[?&#]t=(\d+)/) || u.match(/[?&]start=(\d+)/);
      if (urlT) effectiveStart = parseInt(urlT[1], 10) || 0;
    }
    const base = `https://www.youtube.com/embed/${yt[1]}`;
    return { kind: 'iframe', src: effectiveStart ? `${base}?start=${effectiveStart}` : base };
  }

  // ─ Vimeo ─────────────────────────────────────────────────
  //   https://vimeo.com/12345678
  //   https://player.vimeo.com/video/12345678
  const vim =
    u.match(/vimeo\.com\/(?:video\/)?(\d+)/) ||
    u.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (vim) {
    const base = `https://player.vimeo.com/video/${vim[1]}`;
    return { kind: 'iframe', src: startNum ? `${base}#t=${startNum}s` : base };
  }

  // ─ Direct video files ────────────────────────────────────
  if (/\.(mp4|webm|mov|m3u8)(\?|$)/i.test(u)) {
    // Media-fragments URI: browsers seek to N seconds on load when the URL
    // ends with #t=N. Strip any existing #t= the user may have pasted.
    const cleaned = u.replace(/#t=[^&]*$/i, '');
    return { kind: 'video', src: startNum ? `${cleaned}#t=${startNum}` : cleaned };
  }

  // ─ Embed-ready URL passed through as-is (e.g. a custom iframe URL)
  if (/^https?:\/\/.+\/embed\//i.test(u)) {
    if (!startNum) return { kind: 'iframe', src: u };
    const sep = u.includes('?') ? '&' : '?';
    return { kind: 'iframe', src: `${u}${sep}start=${startNum}` };
  }

  return null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Player shape (orientation)
 *
 * A video block used to be hard-wired to a 16:9 landscape box. Reels / Shorts
 * / phone-shot clips are portrait, and squeezing them into 16:9 left two black
 * pillars around a postage-stamp video. `props.videoOrientation` picks the
 * aspect box, `props.videoWidthPct` how much of the block width the player
 * takes (a full-width 9:16 player would be ~1000px tall on desktop, so
 * portrait defaults to a narrower column) and `props.videoAlign` where that
 * column sits.
 *
 * Legacy blocks carry none of these props → they resolve to the historical
 * landscape / 100% / centered box, so nothing already published moves.
 * ──────────────────────────────────────────────────────────────────────────── */

export type VideoOrientation = 'landscape' | 'portrait' | 'square' | 'fill';

/** height ÷ width for each fixed-ratio orientation */
export const VIDEO_RATIO: Record<'landscape' | 'portrait' | 'square', number> = {
  landscape: 9 / 16,
  portrait: 16 / 9,
  square: 1,
};

export function videoOrientationOf(p: any, fallback: VideoOrientation = 'landscape'): VideoOrientation {
  const o = p?.videoOrientation;
  return o === 'portrait' || o === 'square' || o === 'fill' || o === 'landscape' ? o : fallback;
}

/** Player width as a percentage of the block width (10–100). */
export function videoWidthPctOf(p: any, orientation: VideoOrientation): number {
  const raw = Number(p?.videoWidthPct);
  if (Number.isFinite(raw) && raw > 0) return Math.min(100, Math.max(10, Math.round(raw)));
  return orientation === 'portrait' ? 45 : 100;
}

/**
 * Rendered player height for a block that is `boxWidth` px wide.
 * Returns null for 'fill' (the player takes the authored block height).
 */
export function videoBoxHeight(p: any, boxWidth: number, fallback: VideoOrientation = 'landscape'): number | null {
  const o = videoOrientationOf(p, fallback);
  if (o === 'fill') return null;
  const w = boxWidth * (videoWidthPctOf(p, o) / 100);
  return Math.round(w * VIDEO_RATIO[o]);
}

/**
 * Styles for the two-element player frame:
 *   <div style={outer}><div style={inner}>{iframe|video}</div></div>
 * The iframe/video itself is absolutely positioned to `inset: 0`.
 */
export function videoFrameStyle(
  p: any,
  radius: number,
  fallback: VideoOrientation = 'landscape',
): { outer: React.CSSProperties; inner: React.CSSProperties } {
  const o = videoOrientationOf(p, fallback);
  if (o === 'fill') {
    return {
      outer: { width: '100%', height: '100%' },
      inner: { position: 'relative', width: '100%', height: '100%', borderRadius: radius, overflow: 'hidden' },
    };
  }
  const align = p?.videoAlign === 'left' || p?.videoAlign === 'right' ? p.videoAlign : 'center';
  return {
    outer: {
      width: `${videoWidthPctOf(p, o)}%`,
      // physical margins (not logical) so the choice means the same thing on
      // the RTL Hebrew canvas and an LTR English one
      marginLeft: align === 'center' || align === 'right' ? 'auto' : undefined,
      marginRight: align === 'center' || align === 'left' ? 'auto' : undefined,
    },
    inner: {
      position: 'relative',
      paddingBottom: `${VIDEO_RATIO[o] * 100}%`,
      borderRadius: radius,
      overflow: 'hidden',
    },
  };
}
